import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HideImageIcon from '@mui/icons-material/HideImage';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import type { EmailFile, EmailMessageDetail, EmailParty, GmailAckStatus, Message } from '@/types';
import { EmailAutoActions, type EmailAutoActionUndone } from './EmailAutoActions';
import { EmailSuggestions, type EmailSuggestionDecided } from './EmailSuggestions';
import { EmailHtmlFrame } from './EmailHtmlFrame';
import { sanitizeEmailHtml } from './emailHtml';

interface EmailThreadProps {
  messages: Message[];
  hasOlder?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** An automatic action (#3739) on `messageId` was undone. */
  onAutoActionUndone?: (messageId: string, undone: EmailAutoActionUndone) => void;
  /** A suggestion card (#3740) on `messageId` was accepted, rejected or found expired. */
  onSuggestionDecided?: (messageId: string, decided: EmailSuggestionDecided) => void;
}

const REJECTION_KEYS: Record<string, string> = {
  too_many_attachments: 'emailRejectedTooMany',
  too_large: 'emailRejectedTooLarge',
  unsupported_type: 'emailRejectedType',
  invalid_base64: 'emailRejectedInvalid',
  empty: 'emailRejectedInvalid',
};

/** Gmail acknowledgement of an inbound email (#3742). */
const GMAIL_ACK: Record<GmailAckStatus, { labelKey: string; hintKey: string; color: 'default' | 'success' | 'warning' }> = {
  pending: { labelKey: 'gmailAckPending', hintKey: 'gmailAckPendingHint', color: 'default' },
  done: { labelKey: 'gmailAckDone', hintKey: 'gmailAckDoneHint', color: 'success' },
  not_found: { labelKey: 'gmailAckNotFound', hintKey: 'gmailAckNotFoundHint', color: 'warning' },
  failed: { labelKey: 'gmailAckFailed', hintKey: 'gmailAckFailedHint', color: 'warning' },
};

function partyLabel(party: EmailParty): string {
  return party.name ? `${party.name} <${party.address}>` : party.address;
}

function formatSize(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function EmailFiles({ files }: { files: EmailFile[] }) {
  const { t } = useTranslation('messages');
  const notify = useNotify();
  const [opening, setOpening] = useState<string | null>(null);

  const open = async (file: EmailFile) => {
    setOpening(file.id);
    try {
      const { downloadUrl } = await api.get<{ downloadUrl: string }>(`/attachments/${file.id}/download-url`);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      notify.error(t('emailFileFailed'));
    } finally {
      setOpening(null);
    }
  };

  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {files.map((file) => (
        <Chip
          key={file.id}
          icon={opening === file.id ? <CircularProgress size={14} /> : <AttachFileIcon />}
          label={[file.fileName, formatSize(file.sizeBytes)].filter(Boolean).join(' · ')}
          onClick={() => { void open(file); }}
          variant="outlined"
          aria-label={t('emailOpenFile', { name: file.fileName })}
        />
      ))}
    </Stack>
  );
}

function EmailCard({
  message,
  email,
  onAutoActionUndone,
  onSuggestionDecided,
}: {
  message: Message;
  email: EmailMessageDetail;
  onAutoActionUndone?: EmailThreadProps['onAutoActionUndone'];
  onSuggestionDecided?: EmailThreadProps['onSuggestionDecided'];
}) {
  const { t } = useTranslation('messages');
  const sanitized = useMemo(() => (email.html ? sanitizeEmailHtml(email.html) : null), [email.html]);
  // A reply sent from Messages (#3737).
  const outbound = message.direction === 'outbound';
  const failed = outbound && message.status === 'failed';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        ...(outbound ? { ml: { sm: 6 }, borderColor: failed ? 'error.main' : 'primary.main' } : {}),
      }}
    >
      {!outbound && email.gmailAck && (
        <Box>
          <Tooltip title={t(GMAIL_ACK[email.gmailAck].hintKey)}>
            <Chip
              size="small"
              variant="outlined"
              color={GMAIL_ACK[email.gmailAck].color}
              label={t(GMAIL_ACK[email.gmailAck].labelKey)}
              data-testid="email-gmail-ack"
            />
          </Tooltip>
        </Box>
      )}
      {outbound && (
        <Box>
          <Chip
            size="small"
            color={failed ? 'error' : 'primary'}
            variant="outlined"
            label={failed ? t('emailSendFailedLabel') : t('emailSentFromOsBuddy')}
          />
        </Box>
      )}
      <Box>
        <Typography variant="subtitle2" fontWeight={600}>
          {email.subject || t('emailNoSubject')}
        </Typography>
        <Typography variant="body2">
          {t('emailFrom')}: {partyLabel(email.from)}
        </Typography>
        {email.to.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div">
            {t('emailTo')}: {email.to.map(partyLabel).join(', ')}
          </Typography>
        )}
        {email.cc.length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div">
            {t('emailCc')}: {email.cc.map(partyLabel).join(', ')}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" component="div">
          {format(new Date(message.sentAt), 'd.M.yyyy HH:mm')}
        </Typography>
      </Box>

      {sanitized?.hasRemoteImages && (
        <Alert severity="info" icon={<HideImageIcon fontSize="small" />}>
          {t('emailImagesBlocked')}
        </Alert>
      )}

      {sanitized ? (
        <EmailHtmlFrame sanitizedHtml={sanitized.html} title={t('emailBodyTitle')} />
      ) : (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {message.content}
        </Typography>
      )}

      {(email.htmlTruncated || email.textTruncated) && (
        <Typography variant="caption" color="text.secondary">
          {t('emailTruncated')}
        </Typography>
      )}

      {email.files.length > 0 && <EmailFiles files={email.files} />}

      {email.autoActions && email.autoActions.length > 0 && (
        <EmailAutoActions
          conversationId={message.conversationId}
          actions={email.autoActions}
          onUndone={(undone) => onAutoActionUndone?.(message.id, undone)}
        />
      )}

      {email.suggestions && email.suggestions.length > 0 && (
        <EmailSuggestions
          conversationId={message.conversationId}
          suggestions={email.suggestions}
          onDecided={(decided) => onSuggestionDecided?.(message.id, decided)}
        />
      )}

      {email.rejectedAttachments.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2" fontWeight={600}>
            {t('emailRejectedTitle')}
          </Typography>
          {email.rejectedAttachments.map((rejected) => (
            <Typography key={rejected.name} variant="body2">
              {rejected.name}: {t(REJECTION_KEYS[rejected.reason] ?? 'emailRejectedInvalid')}
            </Typography>
          ))}
        </Alert>
      )}
    </Paper>
  );
}

/** A workshop mailbox thread (#3736): one card per email, oldest first. */
export function EmailThread({
  messages,
  hasOlder = false,
  loadingOlder = false,
  onLoadOlder,
  onAutoActionUndone,
  onSuggestionDecided,
}: EmailThreadProps) {
  const { t } = useTranslation('messages');

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: 'background.default',
      }}
    >
      {hasOlder && onLoadOlder && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            size="small"
            onClick={onLoadOlder}
            disabled={loadingOlder}
            startIcon={loadingOlder ? <CircularProgress size={14} /> : undefined}
          >
            {t('loadOlderMessages')}
          </Button>
        </Box>
      )}
      {messages.map((message) =>
        message.email ? (
          <EmailCard
            key={message.id}
            message={message}
            email={message.email}
            onAutoActionUndone={onAutoActionUndone}
            onSuggestionDecided={onSuggestionDecided}
          />
        ) : (
          // A live event carries no email detail; the thread poll fills it in.
          <Paper key={message.id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {message.content}
            </Typography>
          </Paper>
        ),
      )}
    </Box>
  );
}
